// Centered modal to quickly log a call outcome + remark for a CP,
// without opening the detail drawer. Same API as the drawer form.
import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, Box, Typography, IconButton, Button,
  Select, MenuItem, TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { addCpActivity, CP_SOURCING_OUTCOMES } from "../../Services/CpSourcingApis";
import { C } from "./cpSourcingTheme";

export default function AddActivityModal({ open, cp, onClose, onSaved, onCpSiteVisit }) {
  const loggedInUser = useSelector((s) => s?.Auth?.user);
  const userId = loggedInUser?.userId || loggedInUser?.id;

  const [outcome, setOutcome] = useState("");
  const [remark, setRemark] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setOutcome(""); setRemark(""); }
  }, [open, cp?.cpId]);

  const save = async () => {
    if (!outcome) { toast.error("Select an outcome", { position: "top-center", autoClose: 3000 }); return; }
    if (!remark.trim()) { toast.error("Enter a remark", { position: "top-center", autoClose: 3000 }); return; }
    setSaving(true);
    try {
      const res = await addCpActivity(cp.cpId, { userId, outcome, remark: remark.trim() });
      if (res?.success) {
        toast.success("Activity saved", { position: "top-center", autoClose: 2000 });
        onSaved && onSaved();
        onClose();
      } else {
        toast.error(res?.message || "Failed to save activity", { position: "top-center", autoClose: 3000 });
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to save activity", { position: "top-center", autoClose: 3000 });
    } finally {
      setSaving(false);
    }
  };

  if (!cp) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: "12px" } }}>
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
          <Box>
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: C.txt1, fontFamily: C.sans }}>
              Add Activity
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: C.txt3, fontFamily: C.sans }}>
              Log a call for <b>{cp.cpName || "—"}</b>{" "}
              <span style={{ fontFamily: C.mono, color: C.txt4 }}>#{cp.cpDisplayId}</span>
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
        </Box>

        {/* CP Site Visit — opens the site-visit flow for this CP (handled by the parent).
            A CP can have multiple site visits, so this is always available. */}
        {onCpSiteVisit && (
          <Button
            fullWidth variant="outlined" size="small"
            onClick={() => onCpSiteVisit(cp)}
            sx={{
              mb: 2, textTransform: "none", fontFamily: C.sans, fontWeight: 600, fontSize: 13,
              borderRadius: "8px", borderColor: C.indigoBd, color: C.indigo, background: C.indigoSoft,
              "&:hover": { background: C.indigo, color: "#fff", borderColor: C.indigo },
            }}
          >
            + CP Site Visit
          </Button>
        )}

        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: C.txt2, mb: 0.5, fontFamily: C.sans }}>
          Outcome <span style={{ color: C.red }}>*</span>
        </Typography>
        <Select
          size="small" displayEmpty fullWidth value={outcome}
          onChange={(e) => setOutcome(e.target.value)}
          sx={{ mb: 2, fontSize: 13.5, fontFamily: C.sans, borderRadius: "8px" }}
        >
          <MenuItem value=""><em>Select outcome…</em></MenuItem>
          {CP_SOURCING_OUTCOMES.map((o) => (
            <MenuItem key={o} value={o} sx={{ fontSize: 13.5, fontFamily: C.sans }}>{o}</MenuItem>
          ))}
        </Select>

        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: C.txt2, mb: 0.5, fontFamily: C.sans }}>
          Remark <span style={{ color: C.red }}>*</span>
        </Typography>
        <TextField
          size="small" fullWidth multiline minRows={3}
          placeholder="What was discussed…"
          value={remark} onChange={(e) => setRemark(e.target.value)}
          sx={{ mb: 2.5, "& .MuiOutlinedInput-root": { fontSize: 13.5, fontFamily: C.sans, borderRadius: "8px" } }}
        />

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
          <Button variant="outlined" size="small" onClick={onClose}
            sx={{ textTransform: "none", fontFamily: C.sans, borderColor: C.border, color: C.txt2, borderRadius: "8px" }}>
            Cancel
          </Button>
          <Button variant="contained" size="small" onClick={save} disabled={saving}
            sx={{
              textTransform: "none", fontFamily: C.sans, fontWeight: 600, borderRadius: "8px",
              background: C.indigo, "&:hover": { background: "#3730a3" },
            }}>
            {saving ? "Saving…" : "Save Activity"}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
